import React, { useCallback } from 'react'
import TableRowStatus from 'routes/Transits/Table/TableRowStatus'
import { DeclarationStatusEnum } from 'types/IDeclaration'
import { useTranslation } from 'react-i18next'
import NavigationBarTabs from 'features/declaration/form/components/NavigationBarTabs'
import NavigationBarInfo from 'features/declaration/form/components/NavigationBarInfo'
import TransitEditorActions from './TransitEditorActions'
import NonArrivedMovementReleaseModal from '../../../transit-operation/components/NonArrivedMovementReleaseModal'
import CancelTransitOperationModal from '../../../transit-operation/components/CancelTransitOperationModal'
import FinlandCustomsRequestReplyModal from '../../../transit-operation/components/FinlandCustomsRequestReplyModal'
import FinlandContactFormModal from '../../../transit-operation/components/FinlandContactFormModal'
import ConfirmationModal from '../../../../../components/ConfirmationModal'
import useDeclarationEditorActions from '../../components/useDeclarationEditorActions'
import RequestReleaseModal from '../../../transit-operation/components/RequestReleaseModal'
import ConsignmentSummary from '../../../consignment/components/ConsignmentSummary'
import DeclarationLayout from '../../components/DeclarationLayout'
import TransitDeclarationTabs from './TransitDeclarationTabs'

function TransitEditor() {
  const { t } = useTranslation()
  const {
    form,
    declaration,
    directoTabRef,
    activeTab,
    goToTab,
    interactionsLocked,
    country,

    invokeEditorAction,

    cancellationReasonModalOps,
    grossMassCheckModalOps,
    requestReleaseModalOps,
    nonArrivedMovementReleaseModalOps,
    customsResponseModalOps,
    customsContactModalOps,
    duplicationModal,
  } = useDeclarationEditorActions({ isEns: false })

  const getGrossMassCheckConfirmationMessage = useCallback(() => {
    const grossMassValidationResult = grossMassCheckModalOps.result
    if (!grossMassValidationResult || grossMassValidationResult.valid) return ''

    const { consignmentRequirement } = grossMassValidationResult
    if (consignmentRequirement) {
      return t('declaration.p5.errors.grossMassDifference', {
        actual: consignmentRequirement.actual.toFixed(3),
        expected: consignmentRequirement.expectedMinimum.toFixed(3),
      })
    }

    return t('declaration.p5.errors.grossMassDifference', { context: 'houseConsignment' })
  }, [grossMassCheckModalOps.result, t])

  return (
    <>
      <DeclarationLayout
        interactionsLocked={interactionsLocked}
        headerLeft={<NavigationBarInfo transitOperation={declaration?.transitOperation} declarationKind="transit" />}
        headerCenter={(
          <NavigationBarTabs
            onTabChange={goToTab}
            activeTab={activeTab}
          />
        )}
        footerLeft={<ConsignmentSummary consignmentId={declaration?.consignment?.id ?? null} />}
        footerRight={(
          <>
            <TableRowStatus value={declaration?.transitOperation?.status ?? DeclarationStatusEnum.DRAFT.toString()} asBadge />
            <TransitEditorActions onAction={invokeEditorAction} interactionsLocked={interactionsLocked} />
          </>
        )}
      >
        <TransitDeclarationTabs
          form={form}
          activeTab={activeTab}
          directoTabRef={directoTabRef}
          interactionsLocked={interactionsLocked}
        />
      </DeclarationLayout>
      <RequestReleaseModal
        show={requestReleaseModalOps.isOpen}
        onClose={requestReleaseModalOps.close}
        onSubmit={requestReleaseModalOps.submit}
        isSubmitting={requestReleaseModalOps.isSubmitting}
        isError={requestReleaseModalOps.isError}
      />
      <NonArrivedMovementReleaseModal
        show={nonArrivedMovementReleaseModalOps.isOpen}
        onClose={nonArrivedMovementReleaseModalOps.close}
        onSubmit={nonArrivedMovementReleaseModalOps.submit}
        isSubmitting={nonArrivedMovementReleaseModalOps.isSubmitting}
        isError={nonArrivedMovementReleaseModalOps.isError}
      />
      <CancelTransitOperationModal
        key={declaration?.transitOperation?.id ?? 'new'}
        show={cancellationReasonModalOps.isOpen}
        country={country}
        onClose={cancellationReasonModalOps.dismiss}
        onSubmit={cancellationReasonModalOps.submit}
      />
      <FinlandCustomsRequestReplyModal
        show={customsResponseModalOps.isOpen}
        onClose={customsResponseModalOps.close}
        onSubmit={customsResponseModalOps.submit}
        isSubmitting={customsResponseModalOps.isSubmitting}
        isError={customsResponseModalOps.isError}
      />
      <FinlandContactFormModal
        show={customsContactModalOps.isOpen}
        onClose={customsContactModalOps.close}
        onSubmit={customsContactModalOps.submit}
        isSubmitting={customsContactModalOps.isSubmitting}
        isError={customsContactModalOps.isError}
      />
      <ConfirmationModal
        title={t('declaration.p5.goodsIssues')}
        messageBody={getGrossMassCheckConfirmationMessage()}
        isVisible={grossMassCheckModalOps.isOpen}
        isLoading={false}
        toggleVisibility={grossMassCheckModalOps.reject}
        onConfirmation={grossMassCheckModalOps.confirm}
        onRejection={grossMassCheckModalOps.reject}
      />
      <ConfirmationModal
        title={t('declaration.p5.duplicate')}
        messageBody={t('messages.duplicateDocumentsMessage')}
        warning={t('declaration.p5.unsavedChangesLostWarning')}
        isVisible={duplicationModal.isOpen}
        isLoading={duplicationModal.isSubmitting}
        toggleVisibility={duplicationModal.close}
        onConfirmation={duplicationModal.confirm}
        onRejection={duplicationModal.reject}
        error={duplicationModal.error}
      />
    </>
  )
}

export default TransitEditor
